import React, { useState, useEffect, useCallback } from "react";
import { useOrders } from "../context/OrdersContext";
import OrderDetailsModal from "./OrderDetailsModal";
import { CheckCircle, RefreshCw, X, Printer, Zap, Shield } from "lucide-react";

/* ─── Column config  ────────────────────── */
const COLS = [
  // ── Active  ──
  { id: "placed",                  label: "Placed",                dot: "bg-teal-500",    topBorder: "border-t-teal-500",    bg: "bg-teal-50",    border: "border-teal-200",    ring: "ring-teal-400",    small: false },
  { id: "processing",              label: "Processing",            dot: "bg-blue-500",    topBorder: "border-t-blue-500",    bg: "bg-blue-50",    border: "border-blue-200",    ring: "ring-blue-400",    small: false },
  { id: "printing_started",        label: "Printing",              dot: "bg-indigo-500",  topBorder: "border-t-indigo-500",  bg: "bg-indigo-50",  border: "border-indigo-200",  ring: "ring-indigo-400",  small: true,  icon: Printer,       customOnly: true  },
  { id: "production_completed",    label: "Production Done",       dot: "bg-cyan-500",    topBorder: "border-t-cyan-500",    bg: "bg-cyan-50",    border: "border-cyan-200",    ring: "ring-cyan-400",    small: true,  icon: Zap,           customOnly: true  },
  { id: "quality_check",           label: "Quality Check",         dot: "bg-yellow-500",  topBorder: "border-t-yellow-500",  bg: "bg-yellow-50",  border: "border-yellow-200",  ring: "ring-yellow-400",  small: true,  icon: Shield },
  { id: "packaging",               label: "Packaging",             dot: "bg-amber-500",   topBorder: "border-t-amber-500",   bg: "bg-amber-50",   border: "border-amber-200",   ring: "ring-amber-400",   small: false },
  { id: "shipped",                 label: "Shipped",               dot: "bg-violet-500",  topBorder: "border-t-violet-500",  bg: "bg-violet-50",  border: "border-violet-200",  ring: "ring-violet-400",  small: false },
  // ── Delivered / completed ──
  {
    id: "delivered",
    label: "Delivered",
    dot: "bg-emerald-500",
    topBorder: "border-t-emerald-500",
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    ring: "ring-emerald-400",
    small: true,
    icon: CheckCircle,
  },
  { id: "completed",               label: "Completed",             dot: "bg-emerald-700", topBorder: "border-t-emerald-700", bg: "bg-emerald-50", border: "border-emerald-300", ring: "ring-emerald-500", small: true },
  // ── Return pipeline ──
  { id: "return_requested",        label: "Return Requested",      dot: "bg-sky-500",     topBorder: "border-t-sky-500",     bg: "bg-sky-50",     border: "border-sky-200",     ring: "ring-sky-400",     small: true },
  { id: "return_approved",         label: "Return Approved",       dot: "bg-teal-400",    topBorder: "border-t-teal-400",    bg: "bg-teal-50",    border: "border-teal-200",     ring: "ring-teal-300",   small: true },
  { id: "return_rejected", label: "Return Rejected", dot: "bg-red-500", topBorder: "border-t-red-500", bg: "bg-red-50", border: "border-red-200", ring: "ring-red-400", small: true },
  { id: "returned",                label: "Received Product",              dot: "bg-orange-500",  topBorder: "border-t-orange-500",  bg: "bg-orange-50",  border: "border-orange-200",  ring: "ring-orange-400",  small: false },
  // ── Inspection ──
  { id: "inspection_pending",      label: "Inspection",            dot: "bg-purple-400",  topBorder: "border-t-purple-400",  bg: "bg-purple-50",  border: "border-purple-200",  ring: "ring-purple-400",  small: true },
  // ── Refund / Replacement ──
  { id: "partial_refund",          label: "Partial Refund",        dot: "bg-lime-500",    topBorder: "border-t-lime-500",    bg: "bg-lime-50",    border: "border-lime-200",    ring: "ring-lime-400",    small: true },
  { id: "replacement_processing",  label: "Replacement",           dot: "bg-sky-400",     topBorder: "border-t-sky-400",     bg: "bg-sky-50",     border: "border-sky-200",     ring: "ring-sky-300",     small: true },
  // ── Terminal ──
  
  { id: "refunded",                label: "Refunded",              dot: "bg-green-500",   topBorder: "border-t-green-500",   bg: "bg-green-50",   border: "border-green-200",   ring: "ring-green-400",   small: true },
{ id: "returned_no_refund", label: "No Refund", dot: "bg-red-700", topBorder: "border-t-red-700", bg: "bg-red-50", border: "border-red-300", ring: "ring-red-500", small: true },
  { id: "cancellation_requested", label: "Cancel Requested", dot: "bg-rose-400", topBorder: "border-t-rose-400", bg: "bg-rose-50",  border: "border-rose-200", ring: "ring-rose-300", small: true },
{ id: "cancelled",              label: "Cancelled",        dot: "bg-red-600",  topBorder: "border-t-red-600",  bg: "bg-red-50",   border: "border-red-200",  ring: "ring-red-400",  small: true },  
];

/* ─── Status transitions  */
const STATUS_TRANSITIONS = {
  placed:                 ["processing", "cancellation_requested"],
  processing: ["printing_started", "quality_check", "cancellation_requested"],
  printing_started:       ["production_completed"],                  
  production_completed:   ["quality_check"],                         
  quality_check:          ["packaging"],                           
  packaging:              ["shipped", "cancellation_requested"],      
  shipped:                ["delivered", "returned"],
  delivered:              ["return_requested", "completed"],
  completed:              [],
  return_requested:       ["return_approved", "return_rejected", "returned"],
  return_rejected:  [],
  return_approved:        ["returned"],
  returned:               ["inspection_pending"],
inspection_pending: ["refunded", "partial_refund", "replacement_processing", "returned_no_refund"],
// add terminal:
returned_no_refund: [],
  partial_refund:         [],
  replacement_processing: ["shipped"],
  refunded:               [],
  cancellation_requested: ["cancelled"],                              // admin confirms → final cancel
  cancelled:              [],
  rejected:               [],
};

/* ───  status map ──────────────── */
const STATUS_MAP = {
  placed:                  "Placed",
  processing:              "Processing",
  printing_started:        "Printing Started",
  production_completed:    "Production Completed",
  quality_check:           "Quality Check",
  packaging:               "Packaging",
  shipped:                 "Shipped",
  delivered:               "Delivered",
  completed:               "Completed",
  return_requested:        "Return Requested",
  return_approved:         "Return Approved",
  return_rejected: "Return Rejected",
  returned_no_refund: "Returned (No Refund)",
  returned:                "Returned",
  inspection_pending:      "Inspection Pending",
  partial_refund:          "Partial Refund",
  replacement_processing:  "Replacement Processing",
  refunded:                "Refunded",
  cancellation_requested:  "Cancellation Requested",   
  cancelled:               "Cancelled",                
  rejected:                "Rejected",
  returned_no_refund: "Returned (No Refund)",
};
/* ─── Payment pill styles ──────────────────────────────────────── */
const PAY_PILL = {
  Paid:     "bg-emerald-100 text-emerald-700",
  Pending:  "bg-amber-100 text-amber-700",
  Failed:   "bg-red-100 text-red-700",
  Refunded: "bg-purple-100 text-purple-700",
};

/* ─── Avatar helpers ───────────────────────────────────────────── */
const AVATAR_COLORS = [
  "bg-teal-500","bg-violet-500","bg-pink-500","bg-amber-500",
  "bg-blue-500","bg-emerald-500","bg-red-500","bg-purple-500",
  "bg-sky-500","bg-lime-600",
];
const avatarColor = (str = "") => {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
};
const initials = (name = "") =>
  name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase() || "?";
const fmtDate  = (ds) =>
  ds ? new Date(ds).toLocaleDateString("en-IN", { month:"short", day:"numeric", year:"numeric" }) : "—";


const normaliseColId = (raw = "") => {
  if (!raw) return "placed";
  const s = raw.toLowerCase().trim();
  // direct col-id match
  const allColIds = COLS.map((c) => c.id);
  if (allColIds.includes(s)) return s;
  // known backend variants
  const MAP = {
    "placed":                   "placed",
    "processing":               "processing",
    "processing_started":       "processing",
    "printing started":         "printing_started",
    "printing_started":         "printing_started",
    "production completed":     "production_completed",
    "production_completed":     "production_completed",
    "quality check":            "quality_check",
    "quality_check":            "quality_check",
    "packaging":                "packaging",
    "packed":                   "packaging",
    "shipped":                  "shipped",
    "out_for_delivery":         "shipped",
    "delivered":                "delivered",
    "completed":                "completed",
    "return requested":         "return_requested",
    "return_requested":         "return_requested",
    "return approved":          "return_approved",
    "return_approved":          "return_approved",
"return rejected":          "return_rejected",   

    "returned":                 "returned",
    "return_received":          "returned",
    "inspection pending":       "inspection_pending",
    "inspection_pending":       "inspection_pending",
    "inspection passed":        "inspection_pending",
    "inspection_passed":        "inspection_pending",
    "inspection failed":        "inspection_pending",
    "inspection_failed":        "inspection_pending",
    "partial refund":           "partial_refund",
    "partial_refund":           "partial_refund",
    "replacement processing":   "replacement_processing",
    "replacement_processing":   "replacement_processing",
    "refund":                   "refunded",
    "refunded":                 "refunded",
    "refund_completed":         "refunded",
    "refund_processing":        "refunded",
  "cancelled-refunded":       "cancelled",
"cancelled-notrefunded":    "cancelled",
"cancelled":                "cancelled",
"rejected":                 "rejected",
"returned_no_refund":       "returned_no_refund",

  };
  return MAP[s] || "placed";
};

/* ─── toCard mapper ─────────────────────────────────────────── */
const toCard = (order) => {
  const name = order.customer?.name?.trim() || "Unknown";
  const colId = normaliseColId(order.rawStatus || order.status || "placed");
  const isCustom =
    order.isCustom === true ||
    order.productType === "custom" ||
    (order.items || []).some(
      (i) =>
        i.isCustom === true ||
        i.productType === "custom" ||
        /photo|custom|print|mug|keychain|personali/i.test(i.category || "") ||
        /photo|custom|print|mug|keychain|personali/i.test(i.name     || "")
    );

  return {
    id:           order.orderNumber || order.id,
    orderId:      order.id,
    customer:     name,
    email:        order.customer?.email || "",
    phone:        order.customer?.phone || "",
    date:         fmtDate(order.date),
    payMode:      order.payment_method || "COD",
    payStatus:    order.payment_status || "Pending",
    total:        `₹${(Number(order.total) || 0).toFixed(2)}`,
    initials:     initials(name),
    avatarBg:     avatarColor(name),
    status:       colId,
    isCustom,
    vendor:       order.vendor        || "",
    trackingId:   order.trackingId    || "",
    pickupTime:   order.pickupTime    || "",
    returnDays:   order.returnDays    || 7,
    deliveredOn:  order.deliveredOn   || null,
    returnPolicy: order.returnPolicy  || null,
    returnFlags:  order.returnFlags   || 0,
    items:        order.items         || [],
    rawStatus:    order.rawStatus     || "",
  };
};



/* ─── Toast ─────────────────────────────────────────────────── */
const TOAST_STYLE = {
  success: "bg-emerald-50 border-emerald-200 text-emerald-800",
  error:   "bg-red-50 border-red-200 text-red-800",
  loading: "bg-blue-50 border-blue-200 text-blue-800",
  warning: "bg-amber-50 border-amber-200 text-amber-800",
};

const ToastStack = ({ toasts }) => (
  <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 pointer-events-none max-w-xs w-full px-4 sm:px-0 sm:max-w-sm">
    {toasts.map((t) => (
      <div
        key={t.id}
        className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium shadow-lg border animate-in slide-in-from-bottom-2 ${TOAST_STYLE[t.type] || TOAST_STYLE.success}`}
      >
        {t.type === "loading" && (
          <span className="w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin flex-shrink-0" />
        )}
        <span className="truncate">{t.msg}</span>
      </div>
    ))}
  </div>
);

/* ─── Avatar ────────────────────────────────────────────────── */
const Avatar = ({ name, bg, size = "md" }) => {
  const sz =
    size === "sm" ? "w-5 h-5 text-[8px]" :
    size === "lg" ? "w-10 h-10 text-sm"  : "w-7 h-7 text-[10px]";
  return (
    <div className={`${bg} ${sz} rounded-full flex-shrink-0 flex items-center justify-center text-white font-bold`}>
      {initials(name)}
    </div>
  );
};

/* ─── BigCard ───────────────────────────────────────────────── */
const BigCard = ({ card, updatingOrderId, onDragStart, onDragEnd, onClick }) => {
  const isUpdating = updatingOrderId === card.orderId;

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, card)}
      onDragEnd={onDragEnd}
      onClick={onClick}
      className={`relative bg-white rounded-xl p-3 cursor-grab active:cursor-grabbing
        border border-slate-200 shadow-sm transition-all duration-200
        hover:-translate-y-0.5 hover:shadow-md select-none
        ${isUpdating ? "opacity-50 pointer-events-none" : ""}`}
    >
      {isUpdating && (
        <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-white/70 z-10">
          <span className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Order ID + Custom badge */}
      <div className="mb-2 flex items-center justify-between gap-1">
        <span className="font-mono text-[10px] text-slate-400 bg-slate-50 border border-slate-100 px-1.5 py-0.5 rounded">
          {card.id}
        </span>
        {card.isCustom && (
          <span className="flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-semibold border border-indigo-200">
            <Printer className="w-2 h-2" />Custom
          </span>
        )}
      </div>

      {/* Customer */}
      <div className="flex items-center gap-2 mb-2">
        <Avatar name={card.customer} bg={card.avatarBg} size="md" />
        <div className="min-w-0">
          <p className="text-xs font-semibold text-slate-700 truncate leading-tight">{card.customer}</p>
          <p className="text-[10px] text-slate-400 truncate leading-tight">{card.email}</p>
        </div>
      </div>

      {/* Shipped info */}
      {card.status === "shipped" && card.vendor && (
        <div className="text-[10px] text-slate-400 mb-2 truncate">
          🚚 {card.vendor} · {card.trackingId}
        </div>
      )}

      {/* Items preview */}
      {card.items?.length > 0 && (
        <p className="text-[10px] text-slate-400 mb-2 truncate">
          {card.items.length} item{card.items.length > 1 ? "s" : ""}: {card.items[0]?.name?.slice(0, 28)}{card.items[0]?.name?.length > 28 ? "…" : ""}
        </p>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-2 border-t border-slate-100">
        <span className="font-mono text-xs font-bold text-slate-800">{card.total}</span>
        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${PAY_PILL[card.payStatus] || "bg-slate-100 text-slate-500"}`}>
          {card.payStatus}
        </span>
      </div>

      {card.returnPolicy === "expired" && (
        <p className="text-[9px] text-slate-400 mt-1.5">Return window expired</p>
      )}
    </div>
  );
};

/* ─── SmallCard ─────────────────────────────────────────────── */
const SmallCard = ({ card, updatingOrderId, onClick }) => {
  const isUpdating = updatingOrderId === card.orderId;
  return (
    <div
      onClick={onClick}
      className={`relative bg-white rounded-lg p-2.5 cursor-pointer
        border border-slate-200 shadow-sm transition-all duration-150
        hover:-translate-y-0.5 hover:shadow-md select-none
        ${isUpdating ? "opacity-50 pointer-events-none" : ""}`}
    >
      {isUpdating && (
        <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-white/70 z-10">
          <span className="w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      <div className="flex items-center gap-1.5 mb-1">
        <Avatar name={card.customer} bg={card.avatarBg} size="sm" />
        <p className="text-[11px] font-medium text-slate-700 truncate">{card.customer}</p>
        {card.isCustom && <Printer className="w-2.5 h-2.5 text-indigo-500 flex-shrink-0" />}
      </div>
      <p className="font-mono text-[9px] text-slate-400 mb-1">{card.id}</p>
      <div className="flex items-center justify-between">
        <p className="font-mono text-xs font-bold text-slate-800">{card.total}</p>
        <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${PAY_PILL[card.payStatus] || "bg-slate-100 text-slate-500"}`}>
          {card.payStatus}
        </span>
      </div>
    </div>
  );
};

/* ─── Column ────────────────────────────────────────────────── */
const Column = ({
  col, cards, draggedOverCol, updatingOrderId,
  onDragStart, onDragEnd, onDragOver, onDragEnter, onDragLeave, onDrop,
  onCardClick, onIconClick,
}) => {
  const isOver = draggedOverCol === col.id;
  const Icon   = col.icon;

  return (
    <div className={`flex flex-col flex-shrink-0 h-full ${col.small ? "w-36" : "w-52"}`}>
      {/* Header */}
      <div className="flex items-center gap-1.5 mb-2 px-0.5">
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${col.dot}`} />
        {Icon && (
          <Icon
            onClick={(e) => { e.stopPropagation(); onIconClick?.(col.id); }}
            className="w-3.5 h-3.5 text-emerald-600 cursor-pointer hover:scale-110 transition flex-shrink-0"
          />
        )}
        <span className={`font-bold text-slate-500 uppercase tracking-wider truncate ${col.small ? "text-[9px]" : "text-[10px]"}`}>
          {col.label}
        </span>
        {col.customOnly && (
          <span className="text-[8px] px-1 py-0.5 rounded bg-indigo-100 text-indigo-600 font-semibold flex-shrink-0">
            Custom
          </span>
        )}
        <span className="ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-600 flex-shrink-0">
          {cards.length}
        </span>
      </div>

      {/* Drop zone */}
      <div
        className={`flex-1 overflow-y-auto rounded-xl border border-t-4 p-2 flex flex-col gap-2
          transition-all duration-150
          ${col.bg} ${col.border} ${col.topBorder}
          ${isOver ? `ring-2 ${col.ring} ring-opacity-60` : ""}`}
        onDragOver={onDragOver}
        onDragEnter={() => onDragEnter(col.id)}
        onDragLeave={onDragLeave}
        onDrop={(e) => onDrop(e, col.id)}
        style={{ scrollbarWidth: "thin", scrollbarColor: "#cbd5e1 transparent" }}
      >
        {cards.length === 0 ? (
          <div className="flex flex-col items-center justify-center min-h-[80px] gap-1.5 text-slate-300 select-none">
            <svg width="22" height="22" fill="none" viewBox="0 0 24 24">
              <path d="M20 7H4a2 2 0 00-2 2v9a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2z" stroke="currentColor" strokeWidth="1.5" />
              <path d="M16 3H8L4 7h16l-4-4z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
            </svg>
            <span className="text-[11px]">No orders</span>
          </div>
        ) : (
          cards.map((card) =>
            col.small ? (
              <SmallCard key={card.id} card={card} updatingOrderId={updatingOrderId} onClick={() => onCardClick(card)} />
            ) : (
              <BigCard key={card.id} card={card} updatingOrderId={updatingOrderId} onDragStart={onDragStart} onDragEnd={onDragEnd} onClick={() => onCardClick(card)} />
            )
          )
        )}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════ */

const OrdersKanban = ({
  onDownloadInvoice,
  
  searchTerm: externalSearch = "",
   orders: propOrders,  
  onSearchChange,
}) => {
  const { orders, loading, error, fetchOrders, updateOrderStatus } = useOrders();

  const [selectedColumn,  setSelectedColumn]  = useState(null);
  const [draggedCard,     setDraggedCard]     = useState(null);
  const [draggedOverCol,  setDraggedOverCol]  = useState(null);
  const [activeTab,       setActiveTab]       = useState("placed");
  const [detailOrder,     setDetailOrder]     = useState(null);
  const [updatingOrderId, setUpdatingOrderId] = useState(null);
  const [toasts,          setToasts]          = useState([]);

  /* ── Toasts ── */
  const addToast = useCallback((msg, type = "success") => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, msg, type }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 3500);
  }, []);

  useEffect(() => { fetchOrders(); }, []);

  useEffect(() => {
    if (detailOrder) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [detailOrder]);

  /* ── Column builder ── */
  const buildColumns = (ordersList) => {
    const byStatus = Object.fromEntries(COLS.map((c) => [c.id, []]));
    (ordersList || []).forEach((o) => {
      const card   = toCard(o);
      const colKey = byStatus[card.status] !== undefined ? card.status : "placed";
      byStatus[colKey].push(card);
    });
    return byStatus;
  };

  const filterCards = (cards) => {
    if (!externalSearch) return cards;
    const q = externalSearch.toLowerCase();
    return cards.filter(
      (c) =>
        c.id.toLowerCase().includes(q) ||
        c.customer.toLowerCase().includes(q) ||
        c.email.toLowerCase().includes(q) ||
        c.trackingId.toLowerCase().includes(q)
    );
  };

  const colData = buildColumns(orders);

  /* ── Open order detail modal ── */
  const openOrderDetail = (card) => {
    const fullOrder = orders.find((o) => o.id === card.orderId);
    setDetailOrder(fullOrder || null);
  };

  const handleModalClose = () => setDetailOrder(null);

  /* ── Keep detailOrder in sync ── */
  useEffect(() => {
    if (!detailOrder) return;
    const updated = orders.find((o) => o.id === detailOrder.id);
    if (updated && updated !== detailOrder) setDetailOrder(updated);
  }, [orders]);

  /* ── Drag & drop ── */
  const handleDragStart = (e, card) => {
    setDraggedCard(card);
    e.currentTarget.style.opacity = "0.4";
  };
  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = "1";
    setDraggedCard(null);
    setDraggedOverCol(null);
  };
  const handleDragOver  = (e) => e.preventDefault();
  const handleDragEnter = (colId) => setDraggedOverCol(colId);
  const handleDragLeave = () => setDraggedOverCol(null);

const handleDrop = async (e, targetColId) => {
  e.preventDefault();
  if (!draggedCard || draggedCard.status === targetColId) {
    setDraggedOverCol(null);
    return;
  }

  const allowed = STATUS_TRANSITIONS[draggedCard.status] || [];
  if (!allowed.includes(targetColId)) {
    addToast(`Cannot move from ${draggedCard.status} → ${targetColId}`, "error");
    setDraggedOverCol(null);
    return;
  }

  if (targetColId === "cancellation_requested") {
    const customAllowed = ["placed", "processing"];
    const standardAllowed = ["placed", "processing", "packaging"];

    if (draggedCard.isCustom && !customAllowed.includes(draggedCard.status)) {
      addToast("Custom orders can only be cancelled before printing starts", "error");
      setDraggedOverCol(null);
      return;
    }
    if (!draggedCard.isCustom && !standardAllowed.includes(draggedCard.status)) {
      addToast("Standard orders cannot be cancelled after packaging", "error");
      setDraggedOverCol(null);
      return;
    }
  }

 // Inside handleDrop, replace the cancelled toast block:
if (targetColId === "cancelled") {
  const fullOrder = orders.find((o) => o.id === draggedCard.orderId);
  const payMethod = fullOrder?.payment_method || fullOrder?.paymentMethod || "COD";
  const isPrepaid = !["cod", "cash on delivery", "cash"]
    .includes((payMethod).toLowerCase());

  addToast(
    isPrepaid
      ? "Cancelling order — refund will be initiated automatically…"
      : "Cancelling order — COD, no refund needed…",
    "loading"
  );
}
  // ──────────────────────────────────────────────────────────────

  setDraggedOverCol(null);
  setUpdatingOrderId(draggedCard.orderId);
  if (targetColId !== "cancelled") {
    addToast(`Moving order to ${STATUS_MAP[targetColId] || targetColId}…`, "loading");
  }

 const success = await updateOrderStatus(draggedCard.orderId, targetColId);
  setUpdatingOrderId(null);

 if (success) {
  const fullOrder = orders.find((o) => o.id === draggedCard.orderId);
  const isPrepaid = !["cod", "cash on delivery", "cash"]
    .includes((fullOrder?.payment_method || "cod").toLowerCase());

  addToast(
    targetColId === "cancelled"
      ? isPrepaid
        ? "Order cancelled. Refund initiated to original payment method ✓"
        : "Order cancelled (COD — no refund needed) ✓"
      : `Order moved to ${STATUS_MAP[targetColId]} ✓`
  );
  await fetchOrders();
} else {
    addToast("Failed to update order. Please try again.", "error");
  }
};

  const totalOrders = orders.length;

  if (loading && (!orders || orders.length === 0)) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-slate-100">
        <div className="flex flex-col items-center gap-3 text-slate-400">
          <span className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm">Loading orders…</span>
        </div>
      </div>
    );
  }

  if (error && (!orders || orders.length === 0)) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-slate-100">
        <div className="flex flex-col items-center gap-4 text-red-400 p-6">
          <svg width="40" height="40" fill="none" viewBox="0 0 24 24" className="opacity-40">
            <path d="M20 7H4a2 2 0 00-2 2v9a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2z" stroke="currentColor" strokeWidth="1.5" />
            <path d="M16 3H8L4 7h16l-4-4z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
          </svg>
          <span className="text-sm text-center">{error}</span>
          <button onClick={fetchOrders} className="text-xs px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col bg-slate-100 overflow-hidden">

      {/* ── Top bar ── */}
      <div className="flex-shrink-0 flex items-center gap-2 px-3 py-2 bg-white border-b border-slate-200">
        <span className="hidden sm:inline text-xs text-slate-500 font-medium bg-slate-100 px-2.5 py-1 rounded-lg">
          {totalOrders} orders
        </span>

        {selectedColumn && (
          <button
            onClick={() => setSelectedColumn(null)}
            className="flex items-center gap-1.5 text-xs bg-teal-600 text-white px-3 py-1.5 rounded-lg font-medium hover:bg-teal-700 transition-colors"
          >
            {COLS.find((c) => c.id === selectedColumn)?.label}
            <X className="w-3 h-3" />
          </button>
        )}

        <button
          onClick={fetchOrders}
          disabled={loading}
          className="p-1.5 text-slate-500 hover:text-teal-600 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
          title="Refresh orders"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* ── Mobile column tabs ── */}
      <div className="lg:hidden flex-shrink-0 relative bg-white border-b border-slate-100">
        <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent z-10" />
        <div className="overflow-x-auto" style={{ scrollbarWidth: "none" }}>
          <div className="flex px-3 py-2 gap-1 min-w-max">
            {COLS.map((col) => {
              const count = filterCards(colData[col.id] || []).length;
              return (
                <button
                  key={col.id}
                  onClick={() => setActiveTab(col.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap
                    ${activeTab === col.id ? "bg-teal-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${col.dot}`} />
                  {col.label}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${activeTab === col.id ? "bg-white/25 text-white" : "bg-slate-200 text-slate-600"}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Board ── */}
      <div className="flex-1 min-h-0 relative">

        {/* Desktop */}
        <div
          className="hidden lg:flex absolute inset-0 gap-3 overflow-x-auto overflow-y-hidden p-4 bg-[#fff8ef]"
          style={{ scrollbarWidth: "thin", scrollbarColor: "#cbd5e1 transparent" }}
        >
          {COLS
            .filter((col) => !selectedColumn || col.id === selectedColumn)
            .map((col) => (
              <Column
                key={col.id}
                col={col}
                cards={filterCards(colData[col.id] || [])}
                draggedOverCol={draggedOverCol}
                updatingOrderId={updatingOrderId}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onCardClick={openOrderDetail}
                onIconClick={(colId) => setSelectedColumn(colId)}
              />
            ))}
        </div>

        {/* Mobile — single active column */}
        <div className="lg:hidden absolute inset-0 overflow-y-auto p-4 bg-[#fff8ef]">
          {COLS.filter((col) => col.id === activeTab).map((col) => (
            <Column
              key={col.id}
              col={{ ...col, small: false }}
              cards={filterCards(colData[col.id] || [])}
              draggedOverCol={draggedOverCol}
              updatingOrderId={updatingOrderId}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onCardClick={openOrderDetail}
            />
          ))}
        </div>
      </div>

      {/* ── Order details modal ── */}
      {detailOrder && (
        <OrderDetailsModal
          isOpen={!!detailOrder}
          onClose={handleModalClose}
          order={detailOrder}
          onDownloadInvoice={() => onDownloadInvoice?.(detailOrder)}
        />
      )}

      <ToastStack toasts={toasts} />
    </div>
  );
};

export default OrdersKanban;